import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ApiError, auth, rest, setAccessToken, type Message, type VoiceMember } from '@tasca/client-sdk';
import type { GatewayClient } from '@tasca/client-sdk';
import { activeTyping, useStore } from '@tasca/client-sdk';
import { LIMITS, P, flattenChannels, planChannelMove } from '@tasca/shared';
import type { Channel, Guild } from '@tasca/client-sdk';
import { VoicePanel } from '../voice/VoicePanel.js';
import { VoiceStage } from '../voice/VoiceStage.js';
import { Icon } from '../ui/Icon.js';
import { MemberList } from '../ui/MemberList.js';
import { GuildSettings } from '../ui/GuildSettings.js';
import { MemberRoles } from '../ui/MemberRoles.js';
import { NewChannel, type NewChannelDraft } from '../ui/NewChannel.js';
import { ask, confirm, notify } from '../ui/Overlays.js';
import { ContextMenu, menuFrom, type MenuItem, type MenuRequest } from '../ui/ContextMenu.js';
import { hueOf, initials } from '../lib/avatar.js';
import { has } from '../lib/permissions.js';
import { uploadUrl } from '../lib/media.js';
import {
  loadCollapsed,
  loadMembersVisible,
  saveCollapsed,
  saveMembersVisible,
} from '../lib/layout.js';
import { voiceSupported, type VoiceClient } from '@tasca/client-sdk';

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
}

export function Chat({
  gateway,
  voice,
}: {
  gateway: GatewayClient;
  voice: VoiceClient;
}): React.ReactElement {
  const {
    me,
    status,
    guilds,
    channels,
    messages,
    typing,
    activeGuildId,
    activeChannelId,
    select,
    setHistory,
    addOptimistic,
    failOptimistic,
    reset,
    voiceChannelId,
    voiceStates,
    members,
    consumeVoiceServer,
    unread,
  } = useStore();

  const guildList = useMemo(() => [...guilds.values()], [guilds]);
  const activeGuild = activeGuildId ? (guilds.get(activeGuildId) ?? null) : null;
  const canConfigureGuild =
    has(activeGuild?.permissions, P.MANAGE_GUILD) || has(activeGuild?.permissions, P.MANAGE_ROLES);
  const channelList = activeGuildId ? (channels.get(activeGuildId) ?? []) : [];
  const activeChannel = channelList.find((c) => c.id === activeChannelId) ?? null;
  const thread = activeChannelId ? (messages.get(activeChannelId) ?? []) : [];
  const typers = activeTyping(activeChannelId ? typing.get(activeChannelId) : undefined);

  // Quem está em cada canal de voz. Agrupado uma vez por alteração em vez de
  // varrer o mapa inteiro dentro do `map` dos canais.
  const voiceByChannel = useMemo(() => {
    const out = new Map<string, VoiceMember[]>();
    for (const state of voiceStates.values()) {
      if (!state.channelId) continue;
      const list = out.get(state.channelId);
      if (list) list.push(state);
      else out.set(state.channelId, [state]);
    }
    return out;
  }, [voiceStates]);

  const memberList = activeGuildId ? (members.get(activeGuildId) ?? []) : [];
  /** O nome que esta pessoa usa aqui — nick do guild, senão o nome global. */
  const nameOf = (userId: string): string => {
    if (userId === me?.id) return me.displayName;
    const member = memberList.find((m) => m.userId === userId);
    return member ? (member.nick ?? member.user.displayName) : 'Utilizador';
  };

  const canManageChannels = has(activeChannel?.permissions, P.MANAGE_CHANNELS) ||
    has(activeGuild?.permissions, P.MANAGE_CHANNELS);

  // A coluna como uma lista só, pela ordem em que se vê. A leitura e as
  // contas do arrastar vivem em @tasca/shared, com testes: ver tree.ts.
  const tree = useMemo(
    () => ({
      flat: flattenChannels(channelList),
      categories: channelList.filter((c) => c.type === 2).sort((a, b) => a.position - b.position),
    }),
    [channelList],
  );

  const [draft, setDraft] = useState('');
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showMembers, setShowMembers] = useState(loadMembersVisible);
  const [menu, setMenu] = useState<MenuRequest | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  /** Quem está a ter as permissões mexidas, se alguém. */
  const [rolesFor, setRolesFor] = useState<{ userId: string; name: string } | null>(null);
  /** Categoria onde o canal novo vai cair, ou `false` se não estamos a criar. */
  const [creating, setCreating] = useState<string | null | false>(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(loadCollapsed);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<{ id: string; before: boolean } | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const activeTab = useRef<HTMLButtonElement>(null);
  const lastTypingSent = useRef(0);
  const loadedChannels = useRef(new Set<string>());

  // Carrega histórico uma vez por canal visitado.
  useEffect(() => {
    if (!activeChannelId || loadedChannels.current.has(activeChannelId)) return;
    loadedChannels.current.add(activeChannelId);
    setLoadingHistory(true);
    rest
      .history(activeChannelId)
      .then((r) => setHistory(activeChannelId, r.messages))
      .catch(() => loadedChannels.current.delete(activeChannelId))
      .finally(() => setLoadingHistory(false));
  }, [activeChannelId, setHistory]);

  // Com muitos servidores a barra de separadores rola, e o activo pode estar
  // fora do ecrã — ao arrancar, ou depois de entrar num servidor novo.
  useEffect(() => {
    activeTab.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeGuildId]);

  // Scroll para o fim quando chega algo novo, mas só se o utilizador já
  // estava no fim — senão arrancávamos-lhe a leitura do histórico.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [thread.length]);

  // Subscreve os canais visíveis do guild activo (op 10).
  useEffect(() => {
    if (!activeGuildId) return;
    gateway.subscribe(activeGuildId, channelList.map((c) => c.id));
    gateway.requestMembers(activeGuildId);
  }, [activeGuildId, channelList.length, gateway]);

  // Marca como lido ao chegar ao fim da conversa.
  useEffect(() => {
    const last = thread[thread.length - 1];
    if (!activeChannelId || !last || last.pending) return;
    const t = setTimeout(() => void rest.ack(activeChannelId, last.id).catch(() => {}), 800);
    return () => clearTimeout(t);
  }, [activeChannelId, thread]);

  // Re-render periódico para os indicadores de escrita expirarem.
  useEffect(() => {
    if (typers.length === 0) return;
    const t = setInterval(() => useStore.setState({}), 2000);
    return () => clearInterval(t);
  }, [typers.length]);

  /**
   * O VOICE_SERVER_UPDATE traz endpoint, token e iceServers. Consumimos
   * assim que chega: o token vale 60 segundos.
   */
  useEffect(() => {
    const info = consumeVoiceServer();
    if (!info) return;
    void voice
      .join({
        channelId: info.channelId,
        endpoint: info.endpoint,
        token: info.token,
        iceServers: info.iceServers,
      })
      .catch((err: unknown) => {
        useStore
          .getState()
          .setVoiceState('failed', err instanceof Error ? err.message : 'falhou entrar em voz');
      });
  }, [useStore((st) => st.pendingVoiceServer), consumeVoiceServer, voice]);

  function joinVoice(channelId: string): void {
    const support = voiceSupported();
    if (!support.ok) {
      notify(support.reason ?? 'Voz indisponível neste browser.', 'error');
      return;
    }
    useStore.getState().setVoiceChannel(channelId);
    useStore.getState().setVoiceState('signalling');
    // A API autoriza, aloca o router e responde com VOICE_SERVER_UPDATE.
    // As flags sao as actuais, nao `false`: entrar num canal novo nao deve
    // ligar o microfone de quem o tinha desligado no anterior.
    gateway.send(9, {
      channelId,
      selfMute: voice.muted,
      selfDeaf: voice.deafened,
      selfVideo: false,
      selfStream: false,
    });
  }

  /**
   * Sair da voz. Duas coisas, e as duas sao necessarias: fechar o media e
   * DIZER A API. Sem o op 9 com `channelId: null`, o estado em Redis ficava
   * a dizer que ainda ca estamos e os outros continuavam a ver-nos na lista
   * ate a varredura de fantasmas passar.
   */
  async function leaveVoice(): Promise<void> {
    gateway.send(9, {
      channelId: null,
      selfMute: false,
      selfDeaf: false,
      selfVideo: false,
      selfStream: false,
    });
    await voice.leave();
    useStore.getState().clearVoice();
  }

  async function send(): Promise<void> {
    const content = draft.trim();
    if (!content || !activeChannelId || !me) return;

    const nonce = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setDraft('');

    // Bolha óptimista. É provisória até o MESSAGE_CREATE com o mesmo nonce
    // chegar pelo gateway e a substituir — ver ADR-006.
    const optimistic: Message = {
      id: `pending-${nonce}`,
      channelId: activeChannelId,
      guildId: activeGuildId,
      author: me,
      content,
      type: 0,
      flags: 0,
      createdAt: new Date().toISOString(),
      editedAt: null,
      referenceId: null,
      mentions: [],
      mentionEveryone: false,
      pinned: false,
      attachments: [],
      reactions: [],
      nonce,
      pending: true,
    };
    addOptimistic(optimistic);

    try {
      await rest.send(activeChannelId, { content, nonce });
    } catch (err) {
      failOptimistic(activeChannelId, nonce);
      if (err instanceof ApiError && err.code === 'rate_limited') {
        const retry = (err.details as { retryAfter?: number } | undefined)?.retryAfter;
        setDraft(content);
        notify(`Demasiado depressa. Tenta em ${retry ?? 5}s.`, 'error');
      }
    }
  }

  function onDraftChange(value: string): void {
    setDraft(value);
    // Debounce de 4s: o servidor limita a 10/min, e mandar um por tecla
    // gastava o orçamento em dois segundos.
    const now = Date.now();
    if (activeChannelId && value.length > 0 && now - lastTypingSent.current > 4000) {
      lastTypingSent.current = now;
      void rest.typing(activeChannelId).catch(() => {});
    }
  }

  async function createGuild(): Promise<void> {
    const name = await ask({
      title: 'Criar servidor',
      label: 'Nome',
      placeholder: 'A Minha Tasca',
      confirm: 'Criar',
      maxLength: LIMITS.guildNameMax,
    });
    if (!name) return;
    try {
      await rest.createGuild(name);
      // O GUILD_CREATE chega pelo gateway; não mexemos no store aqui.
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou criar o servidor', 'error');
    }
  }

  async function createChannel(draft: NewChannelDraft): Promise<void> {
    if (!activeGuildId) return;
    setCreating(false);
    try {
      const channel = await rest.createChannel(activeGuildId, {
        name: draft.name,
        type: draft.type,
        parentId: draft.parentId,
      });
      // Um canal de texto novo abre-se; uma categoria não se abre, e um de voz
      // não arrasta ninguém para dentro da voz sem o pedir.
      if (channel.type === 0) select(activeGuildId, channel.id);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou criar o canal', 'error');
    }
  }

  function toggleCategory(id: string): void {
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveCollapsed(next);
      return next;
    });
  }

  /**
   * Largar um canal em cima (ou por baixo) de outro.
   *
   * As contas são do `planChannelMove`, que tem testes; aqui só se envia o que
   * mudou — e num pedido só, porque numa sequência de pedidos independentes
   * uma falha a meio deixava a coluna numa ordem que ninguém escolheu.
   */
  async function moveChannel(draggedId: string, targetId: string, before: boolean): Promise<void> {
    if (!activeGuildId) return;
    const plan = planChannelMove(channelList, draggedId, targetId, before);
    if (plan.length === 0) return;
    try {
      await rest.reorderChannels(activeGuildId, plan);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou mudar a ordem', 'error');
    }
  }

  /** Os atributos de arrastar, iguais para um canal e para uma categoria. */
  function dragProps(channel: Channel): Record<string, unknown> {
    if (!canManageChannels) return {};
    return {
      draggable: true,
      onDragStart: (e: React.DragEvent) => {
        setDragging(channel.id);
        e.dataTransfer.effectAllowed = 'move';
        // Sem isto o Firefox não começa a arrastar de todo.
        e.dataTransfer.setData('text/plain', channel.id);
      },
      onDragEnd: () => {
        setDragging(null);
        setDropAt(null);
      },
      onDragOver: (e: React.DragEvent) => {
        if (!dragging || dragging === channel.id) return;
        e.preventDefault();
        const box = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setDropAt({ id: channel.id, before: e.clientY < box.top + box.height / 2 });
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        const target = dropAt;
        setDragging(null);
        setDropAt(null);
        if (target) void moveChannel(e.dataTransfer.getData('text/plain'), target.id, target.before);
      },
    };
  }

  function dropClass(channel: Channel): string {
    if (dropAt?.id !== channel.id) return '';
    return dropAt.before ? 'drop-before' : 'drop-after';
  }

  async function invite(channelId: string | null = activeChannelId): Promise<void> {
    if (!channelId) return;
    try {
      const r = await rest.createInvite(channelId);
      const url = `${location.origin}/invite/${r.code}`;
      // Sem permissão para a área de transferência o link tem de aparecer,
      // senão o convite morria aqui a dizer que estava copiado.
      const copied = await navigator.clipboard
        .writeText(url)
        .then(() => true)
        .catch(() => false);
      notify(copied ? `Convite copiado:\n${url}` : `Convite criado:\n${url}`);
    } catch {
      notify('Falhou criar o convite', 'error');
    }
  }

  async function joinByCode(): Promise<void> {
    const code = await ask({
      title: 'Entrar com convite',
      label: 'Código ou link do convite',
      placeholder: 'abc123',
      confirm: 'Entrar',
    });
    if (!code) return;
    try {
      // Aceita o link inteiro colado: o código é o que vem depois da barra.
      await rest.acceptInvite(code.replace(/^.*\//, ''));
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Convite inválido', 'error');
    }
  }

  // ─── Menus de botão direito ──────────────────────────────────────────────
  //
  // Cada menu mostra o que se pode fazer ÀQUELE alvo. A filtragem por
  // permissões é para não oferecer botões que a API vai recusar — quem decide
  // continua a ser o servidor, e um não dele vira aviso no canto.

  function guildMenu(event: React.MouseEvent, g: Guild): void {
    const items: MenuItem[] = [];
    if (has(g.permissions, P.MANAGE_GUILD) || has(g.permissions, P.MANAGE_ROLES)) {
      items.push({
        label: 'Definições do servidor',
        icon: 'settings',
        onSelect: () => {
          select(g.id, (channels.get(g.id) ?? []).find((c) => c.type === 0)?.id ?? null);
          setSettingsOpen(true);
        },
      });
    }
    if (has(g.permissions, P.MANAGE_CHANNELS)) {
      items.push({ label: 'Criar canal', icon: 'plus', onSelect: () => setCreating(null) });
    }
    items.push({ separator: true });
    items.push({
      label: 'Sair do servidor',
      icon: 'logout',
      danger: true,
      onSelect: () => void leaveGuild(g),
    });
    setMenu(menuFrom(event, g.name, items));
  }

  async function leaveGuild(g: Guild): Promise<void> {
    if (!me) return;
    const sure = await confirm({
      title: `Sair de ${g.name}?`,
      body: 'Para voltar precisas de um convite novo.',
      confirm: 'Sair',
      danger: true,
    });
    if (!sure) return;
    try {
      await rest.kickMember(g.id, me.id);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou sair do servidor', 'error');
    }
  }

  function channelMenu(event: React.MouseEvent, c: Channel): void {
    const manage = has(c.permissions, P.MANAGE_CHANNELS);
    const items: MenuItem[] = [];

    if (c.type === 1) {
      items.push(
        c.id === voiceChannelId
          ? { label: 'Sair da voz', icon: 'phoneOff', onSelect: () => void leaveVoice() }
          : { label: 'Entrar em voz', icon: 'volume', onSelect: () => joinVoice(c.id) },
      );
    } else {
      items.push({ label: 'Abrir', icon: 'hash', onSelect: () => select(activeGuildId, c.id) });
    }

    if (c.type === 2) {
      items.push({
        label: 'Criar canal aqui',
        icon: 'plus',
        onSelect: () => setCreating(c.id),
      });
    } else {
      items.push({ label: 'Criar convite', icon: 'userPlus', onSelect: () => void invite(c.id) });
    }

    if (manage) {
      items.push({ separator: true });
      items.push({ label: 'Mudar o nome', icon: 'pencil', onSelect: () => void renameChannel(c) });
      if (c.type === 0) {
        items.push({ label: 'Mudar o tópico', icon: 'pencil', onSelect: () => void retopic(c) });
      }
      items.push({ separator: true });
      items.push({
        label: 'Apagar canal',
        icon: 'trash',
        danger: true,
        onSelect: () => void removeChannel(c),
      });
    }

    setMenu(menuFrom(event, `${c.type === 1 ? '♪' : '#'} ${c.name ?? ''}`, items));
  }

  async function renameChannel(c: Channel): Promise<void> {
    const name = await ask({
      title: 'Mudar o nome do canal',
      label: 'Nome',
      initial: c.name ?? '',
      confirm: 'Guardar',
      maxLength: LIMITS.channelNameMax,
    });
    if (!name || name === c.name) return;
    try {
      await rest.updateChannel(c.id, { name: c.type === 0 ? name.toLowerCase() : name });
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou mudar o nome', 'error');
    }
  }

  async function retopic(c: Channel): Promise<void> {
    const topic = await ask({
      title: 'Tópico do canal',
      label: 'Sobre o que é este canal',
      initial: c.topic ?? '',
      placeholder: 'Deixa vazio para tirar o tópico',
      confirm: 'Guardar',
      maxLength: LIMITS.channelTopicMax,
      multiline: true,
    });
    if (topic === null) return;
    try {
      await rest.updateChannel(c.id, { topic: topic.length > 0 ? topic : null });
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou mudar o tópico', 'error');
    }
  }

  async function removeChannel(c: Channel): Promise<void> {
    const sure = await confirm({
      title: `Apagar ${c.name}?`,
      body: 'As mensagens deste canal vão com ele, e isto não se desfaz.',
      confirm: 'Apagar canal',
      danger: true,
    });
    if (!sure) return;
    try {
      await rest.deleteChannel(c.id);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou apagar o canal', 'error');
    }
  }

  function messageMenu(event: React.MouseEvent, m: Message): void {
    const mine = m.author?.id === me?.id;
    const canManage = has(activeChannel?.permissions, P.MANAGE_MESSAGES);
    const items: MenuItem[] = [
      {
        label: 'Reagir com 👍',
        icon: 'smile',
        onSelect: () => void rest.react(m.channelId, m.id, '👍').catch(() => {}),
      },
      {
        label: 'Copiar texto',
        icon: 'copy',
        onSelect: () => {
          void navigator.clipboard
            .writeText(m.content)
            .then(() => notify('Mensagem copiada'))
            .catch(() => notify('O browser não deixou copiar', 'error'));
        },
      },
    ];

    if (mine) {
      items.push({ label: 'Editar mensagem', icon: 'pencil', onSelect: () => void editMessage(m) });
    }
    if (mine || canManage) {
      items.push({ separator: true });
      items.push({
        label: 'Apagar mensagem',
        icon: 'trash',
        danger: true,
        onSelect: () => void removeMessage(m),
      });
    }

    setMenu(menuFrom(event, m.author?.displayName ?? 'Mensagem', items));
  }

  async function editMessage(m: Message): Promise<void> {
    const content = await ask({
      title: 'Editar mensagem',
      label: 'Mensagem',
      initial: m.content,
      confirm: 'Guardar',
      maxLength: LIMITS.messageContentMax,
      multiline: true,
    });
    if (content === null || content === m.content) return;
    try {
      await rest.editMessage(m.channelId, m.id, content);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou editar a mensagem', 'error');
    }
  }

  async function removeMessage(m: Message): Promise<void> {
    const sure = await confirm({
      title: 'Apagar a mensagem?',
      body: m.content.slice(0, 140),
      confirm: 'Apagar',
      danger: true,
    });
    if (!sure) return;
    try {
      await rest.deleteMessage(m.channelId, m.id);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou apagar a mensagem', 'error');
    }
  }

  function userMenu(event: React.MouseEvent, userId: string, name: string): void {
    const isMe = userId === me?.id;
    const guildPermissions = activeGuildId ? guilds.get(activeGuildId)?.permissions : undefined;
    const items: MenuItem[] = [
      {
        label: 'Mencionar',
        icon: 'at',
        onSelect: () =>
          setDraft((d) => `${d}${d.length > 0 && !d.endsWith(' ') ? ' ' : ''}@${name} `),
      },
    ];

    if (has(guildPermissions, isMe ? P.CHANGE_NICKNAME : P.MANAGE_NICKNAMES)) {
      items.push({
        label: 'Mudar a alcunha',
        icon: 'pencil',
        onSelect: () => void setNick(userId, name),
      });
    }

    // Dar e tirar permissões a ESTA pessoa. Pelo lado do grupo faz-se nas
    // definições; a pergunta do dia-a-dia é a de cá, sobre alguém concreto.
    if (has(guildPermissions, P.MANAGE_ROLES)) {
      items.push({
        label: 'Permissões…',
        icon: 'settings',
        onSelect: () => setRolesFor({ userId, name }),
      });
    }

    // Moderação de voz: só faz sentido para quem ESTÁ em voz, e as permissões
    // que contam são as do canal onde essa pessoa está, não as do guild.
    const state = voiceStates.get(userId);
    const voiceChannel = state?.channelId
      ? channelList.find((c) => c.id === state.channelId)
      : undefined;
    if (!isMe && state?.channelId && voiceChannel && activeGuildId) {
      const guildId = activeGuildId;
      if (has(voiceChannel.permissions, P.MUTE_MEMBERS)) {
        items.push({
          label: state.serverMute ? 'Devolver o microfone' : 'Silenciar na voz',
          icon: 'micOff',
          onSelect: () =>
            void rest
              .moderateVoice(guildId, userId, { serverMute: !state.serverMute })
              .catch(() => notify('Falhou silenciar', 'error')),
        });
      }
      if (has(voiceChannel.permissions, P.DEAFEN_MEMBERS)) {
        items.push({
          label: state.serverDeaf ? 'Devolver o áudio' : 'Ensurdecer na voz',
          icon: 'headphonesOff',
          onSelect: () =>
            void rest
              .moderateVoice(guildId, userId, { serverDeaf: !state.serverDeaf })
              .catch(() => notify('Falhou ensurdecer', 'error')),
        });
      }
      if (has(voiceChannel.permissions, P.MOVE_MEMBERS)) {
        items.push({
          label: 'Desligar da voz',
          icon: 'phoneOff',
          danger: true,
          onSelect: () =>
            void rest
              .disconnectVoice(guildId, userId)
              .catch(() => notify('Falhou desligar da voz', 'error')),
        });
      }
    }

    if (!isMe && has(guildPermissions, P.KICK_MEMBERS)) {
      items.push({ separator: true });
      items.push({
        label: 'Expulsar do servidor',
        icon: 'userX',
        danger: true,
        onSelect: () => void kick(userId, name),
      });
    }
    if (!isMe && has(guildPermissions, P.BAN_MEMBERS)) {
      items.push({
        label: 'Banir',
        icon: 'ban',
        danger: true,
        onSelect: () => void ban(userId, name),
      });
    }

    setMenu(menuFrom(event, name, items));
  }

  async function setNick(userId: string, name: string): Promise<void> {
    if (!activeGuildId) return;
    const guildId = activeGuildId;
    const current = (members.get(guildId) ?? []).find((m) => m.userId === userId);
    const nick = await ask({
      title: `Alcunha de ${name}`,
      label: 'Como aparece neste servidor',
      initial: current?.nick ?? '',
      placeholder: 'Deixa vazio para usar o nome dele',
      confirm: 'Guardar',
      maxLength: LIMITS.displayNameMax,
    });
    if (nick === null) return;
    try {
      await rest.updateMember(guildId, userId, { nick: nick.length > 0 ? nick : null });
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou mudar a alcunha', 'error');
    }
  }

  async function kick(userId: string, name: string): Promise<void> {
    if (!activeGuildId) return;
    const sure = await confirm({
      title: `Expulsar ${name}?`,
      body: 'Pode voltar a entrar com um convite novo.',
      confirm: 'Expulsar',
      danger: true,
    });
    if (!sure) return;
    try {
      await rest.kickMember(activeGuildId, userId);
      notify(`${name} foi expulso.`);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou expulsar', 'error');
    }
  }

  async function ban(userId: string, name: string): Promise<void> {
    if (!activeGuildId) return;
    const sure = await confirm({
      title: `Banir ${name}?`,
      body: 'Sai do servidor e não volta a entrar, nem com convite.',
      confirm: 'Banir',
      danger: true,
    });
    if (!sure) return;
    try {
      await rest.banMember(activeGuildId, userId);
      notify(`${name} foi banido.`);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou banir', 'error');
    }
  }

  async function logout(): Promise<void> {
    await leaveVoice().catch(() => {});
    gateway.disconnect();
    await auth.logout().catch(() => {});
    setAccessToken(null);
    reset();
  }

  return (
    <div className={`shell ${showMembers ? 'with-members' : ''}`}>
      {/* ─── Servidores ─────────────────────────────────────────────── */}
      <nav className="tabs" aria-label="Servidores">
        <div
          className="tabs-list"
          // A roda do rato não rola sozinha na horizontal. Sem isto, e com a
          // barra de rolagem escondida, os servidores que ficam fora do ecrã
          // não se alcançam com um rato — só com trackpad.
          onWheel={(e) => {
            if (e.deltaY === 0) return;
            e.currentTarget.scrollLeft += e.deltaY;
          }}
        >
          {guildList.map((g) => (
            <button
              key={g.id}
              ref={g.id === activeGuildId ? activeTab : null}
              className={`tab ${g.id === activeGuildId ? 'on' : ''}`}
              style={{ ['--hue' as string]: hueOf(g.id) }}
              // `aria-current` e não `role="tab"`: por baixo não há um painel
              // por servidor, há a aplicação inteira a mudar de conteúdo.
              aria-current={g.id === activeGuildId ? 'page' : undefined}
              title={g.name}
              onClick={() => {
                const first = (channels.get(g.id) ?? []).find((c) => c.type === 0);
                select(g.id, first?.id ?? null);
              }}
              onContextMenu={(e) => guildMenu(e, g)}
            >
              <span className="tab-chip">
                {g.iconKey ? <img src={uploadUrl(g.iconKey)} alt="" /> : initials(g.name)}
              </span>
              <span className="tab-name">{g.name}</span>
            </button>
          ))}
        </div>

        {/* Fora da lista que rola: com muitos servidores, estes continuam cá. */}
        <div className="tabs-actions">
          <button className="icon" title="Criar servidor" onClick={createGuild}>
            <Icon name="plus" />
          </button>
          <button className="icon" title="Entrar com convite" onClick={joinByCode}>
            <Icon name="login" />
          </button>
        </div>
      </nav>

      {/* ─── Canais ──────────────────────────────────────────────────── */}
      <aside className="channels">
        <header
          className={`channels-head ${activeGuild?.bannerKey ? 'with-banner' : ''}`}
          style={
            activeGuild?.bannerKey
              ? { backgroundImage: `url(${uploadUrl(activeGuild.bannerKey)})` }
              : undefined
          }
          onContextMenu={(e) => activeGuild && guildMenu(e, activeGuild)}
        >
          <span>{activeGuild?.name ?? 'Tasca'}</span>
          {canConfigureGuild && (
            <button
              className="icon"
              title="Definições do servidor"
              onClick={() => setSettingsOpen(true)}
            >
              <Icon name="settings" />
            </button>
          )}
        </header>

        <div className="channels-list">
          {channelList.length === 0 && <p className="hint">Sem canais visíveis.</p>}
          {/* Cada linha é um sítio onde se pode largar outra: ver `dragProps`. */}
          {tree.flat.map((c) => {
            if (c.type === 2) {
              const shut = collapsed.has(c.id);
              return (
                <button
                  key={c.id}
                  className={`category ${shut ? 'shut' : ''} ${dropClass(c)} ${
                    dragging === c.id ? 'dragging' : ''
                  }`}
                  onClick={() => toggleCategory(c.id)}
                  onContextMenu={(e) => channelMenu(e, c)}
                  aria-expanded={!shut}
                  {...dragProps(c)}
                >
                  <span className="category-arrow">
                    <Icon name="chevron" />
                  </span>
                  <span className="channel-name">{c.name}</span>
                </button>
              );
            }

            // Uma gaveta fechada esconde o que lá está dentro — menos o canal
            // aberto e aquele onde estamos em voz, que desapareceriam da vista
            // sem deixar de estar a acontecer.
            const hidden =
              c.parentId !== null &&
              collapsed.has(c.parentId) &&
              c.id !== activeChannelId &&
              c.id !== voiceChannelId &&
              (unread.get(c.id) ?? 0) === 0;
            if (hidden) return null;

            // Quem está neste canal de voz, visível SEM entrar: é isso que
            // decide se vale a pena. Por nome, que a ordem de chegada muda
            // debaixo dos olhos de quem está a ler.
            const here = (voiceByChannel.get(c.id) ?? [])
              .map((state) => ({ state, name: nameOf(state.userId) }))
              .sort((a, b) => a.name.localeCompare(b.name, 'pt-PT'));

            return (
              <Fragment key={c.id}>
                <button
                  className={`channel ${c.id === activeChannelId ? 'on' : ''} ${
                    c.id === voiceChannelId ? 'connected' : ''
                  } ${dropClass(c)} ${dragging === c.id ? 'dragging' : ''}`}
                  {...dragProps(c)}
                  // Um canal de voz abre como qualquer outro — é um sítio com
                  // conversa — e entrar na voz é a consequência de o abrir.
                  // Quem já lá está só volta a vê-lo, sem reentrar.
                  onClick={() => {
                    select(activeGuildId, c.id);
                    if (c.type === 1 && c.id !== voiceChannelId) joinVoice(c.id);
                  }}
                  onContextMenu={(e) => channelMenu(e, c)}
                >
                  <span className="sigil">
                    <Icon name={c.type === 1 ? 'volume' : 'hash'} />
                  </span>
                  <span className="channel-name">{c.name}</span>
                  {/* Actividade que ainda nao vimos. No desktop este mesmo numero
                      vai para o badge do tray — ver lib/desktopBootstrap.ts. */}
                  {(unread.get(c.id) ?? 0) > 0 && (
                    <span className="unread-badge">{unread.get(c.id)}</span>
                  )}
                </button>

                {here.length > 0 && (
                  <ul className="channel-voice">
                    {here.map(({ state, name }) => (
                      <li
                        key={state.userId}
                        onContextMenu={(e) => userMenu(e, state.userId, name)}
                      >
                        <span
                          className="avatar"
                          style={{ ['--hue' as string]: hueOf(state.userId) }}
                        >
                          {initials(name)}
                        </span>
                        <span className="channel-voice-name">{name}</span>
                        {(state.selfMute || state.serverMute) && (
                          <span
                            className={`voice-flag ${state.serverMute ? 'mod' : ''}`}
                            title={
                              state.serverMute
                                ? 'silenciado por moderação'
                                : 'sem microfone'
                            }
                          >
                            <Icon name="micOff" />
                          </span>
                        )}
                        {(state.selfDeaf || state.serverDeaf) && (
                          <span className="voice-flag" title="sem áudio">
                            <Icon name="headphonesOff" />
                          </span>
                        )}
                        {state.selfStream && (
                          <span className="voice-flag live" title="a partilhar ecrã">
                            <Icon name="screenShare" />
                          </span>
                        )}
                        {state.selfVideo && (
                          <span className="voice-flag live" title="com câmara">
                            <Icon name="video" />
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </Fragment>
            );
          })}
          {activeGuildId && canManageChannels && (
            <button className="channel add" onClick={() => setCreating(null)}>
              <span className="sigil">
                <Icon name="plus" />
              </span>
              <span>novo canal</span>
            </button>
          )}
        </div>

        <VoicePanel
          voice={voice}
          onLeave={() => void leaveVoice()}
          channelName={
            channelList.find((c) => c.id === voiceChannelId)?.name ?? null
          }
        />

        <footer className="me">
          <span className="avatar" style={{ ['--hue' as string]: hueOf(me?.id ?? '0') }}>
            {initials(me?.displayName ?? '?')}
          </span>
          <span className="me-name">
            <strong>{me?.displayName}</strong>
            <small className={`conn ${status}`}>
              {status === 'ready'
                ? 'ligado'
                : status === 'reconnecting'
                  ? 'a reconectar…'
                  : status === 'connecting'
                    ? 'a ligar…'
                    : status}
            </small>
          </span>
          <button className="icon" title="Sair" onClick={logout}>
            <Icon name="logout" />
          </button>
        </footer>
      </aside>

      {/* ─── Conversa ────────────────────────────────────────────────── */}
      <main className="thread">
        <header className="thread-head">
          <span className="sigil">
            <Icon name={activeChannel?.type === 1 ? 'volume' : 'hash'} />
          </span>
          <strong>{activeChannel?.name ?? '—'}</strong>
          {activeChannel?.topic && <span className="topic">{activeChannel.topic}</span>}
          <div className="spacer" />
          <button
            className={`icon members-toggle ${showMembers ? 'on' : ''}`}
            title={showMembers ? 'Esconder membros' : 'Mostrar membros'}
            aria-pressed={showMembers}
            onClick={() => {
              setShowMembers((v) => {
                saveMembersVisible(!v);
                return !v;
              });
            }}
          >
            <Icon name="users" />
          </button>
          {activeChannelId && (
            <button className="icon" title="Criar convite" onClick={() => void invite()}>
              <Icon name="userPlus" />
            </button>
          )}
        </header>

        {activeChannel?.type === 1 && (
          <VoiceStage
            channel={activeChannel}
            voice={voice}
            connected={activeChannel.id === voiceChannelId}
            onJoin={() => joinVoice(activeChannel.id)}
            onLeave={() => void leaveVoice()}
            onUserMenu={userMenu}
          />
        )}

        <div className="messages" ref={scroller}>
          {loadingHistory && <p className="hint">A carregar…</p>}
          {!loadingHistory && thread.length === 0 && activeChannel && (
            <div className="empty">
              <h2>
                {activeChannel.type === 1 ? '♪ ' : '#'}
                {activeChannel.name}
              </h2>
              <p>
                {activeChannel.type === 1
                  ? 'A conversa escrita do canal de voz. Serve para o link, para o "já venho", e para quem está sem microfone.'
                  : 'É o início deste canal. Diz algo.'}
              </p>
            </div>
          )}

          {thread.map((m, i) => {
            const prev = thread[i - 1];
            // Agrupa mensagens seguidas do mesmo autor dentro de 5 minutos.
            const grouped =
              prev !== undefined &&
              prev.author?.id === m.author?.id &&
              new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < 300_000;

            return (
              <article
                key={m.id}
                className={`msg ${grouped ? 'grouped' : ''} ${m.pending ? 'pending' : ''} ${
                  m.failed ? 'failed' : ''
                }`}
                onContextMenu={(e) => messageMenu(e, m)}
              >
                {!grouped && (
                  <span
                    className="avatar"
                    style={{ ['--hue' as string]: hueOf(m.author?.id ?? '0') }}
                    // O autor é um alvo próprio: botão direito nele são as
                    // acções sobre a PESSOA, não sobre a mensagem.
                    onContextMenu={(e) =>
                      m.author && userMenu(e, m.author.id, m.author.displayName)
                    }
                  >
                    {initials(m.author?.displayName ?? '?')}
                  </span>
                )}
                <div className="msg-body">
                  {!grouped && (
                    <div className="msg-head">
                      <strong
                        onContextMenu={(e) =>
                          m.author && userMenu(e, m.author.id, m.author.displayName)
                        }
                      >
                        {m.author?.displayName ?? 'Utilizador apagado'}
                      </strong>
                      <time>{timeOf(m.createdAt)}</time>
                      {m.editedAt && <span className="edited">editada</span>}
                    </div>
                  )}
                  <p className="msg-text">{m.content}</p>
                  {m.failed && <span className="fail">não foi enviada</span>}
                  {m.reactions.length > 0 && (
                    <div className="reactions">
                      {m.reactions.map((r) => (
                        <button
                          key={r.emoji}
                          className={`reaction ${r.me ? 'mine' : ''}`}
                          onClick={() =>
                            void (r.me
                              ? rest.unreact(m.channelId, m.id, r.emoji)
                              : rest.react(m.channelId, m.id, r.emoji)
                            ).catch(() => {})
                          }
                        >
                          {r.emoji} {r.count}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {!m.pending && (
                  <div className="msg-actions">
                    <button
                      title="Reagir"
                      onClick={() => void rest.react(m.channelId, m.id, '👍').catch(() => {})}
                    >
                      <Icon name="smile" />
                    </button>
                    {m.author?.id === me?.id && (
                      <button
                        title="Apagar"
                        onClick={() =>
                          void rest.deleteMessage(m.channelId, m.id).catch(() => {})
                        }
                      >
                        <Icon name="trash" />
                      </button>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <div className="composer-wrap">
          <div className="typing">
            {typers.length > 0 &&
              `${typers.map((t) => t.displayName).join(', ')} ${
                typers.length === 1 ? 'está' : 'estão'
              } a escrever…`}
          </div>
          <form
            className="composer"
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            <input
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              placeholder={
                activeChannel ? `Mensagem em #${activeChannel.name}` : 'Escolhe um canal'
              }
              disabled={!activeChannelId}
              maxLength={4000}
            />
            <button type="submit" disabled={!draft.trim() || !activeChannelId}>
              Enviar
            </button>
          </form>
        </div>
      </main>

      {/* ─── Membros ────────────────────────────────────── */}
      {menu && <ContextMenu request={menu} onClose={() => setMenu(null)} />}

      {settingsOpen && activeGuild && (
        <GuildSettings guild={activeGuild} onClose={() => setSettingsOpen(false)} />
      )}

      {creating !== false && (
        <NewChannel
          categories={tree.categories}
          parentId={creating}
          onCreate={(draft) => void createChannel(draft)}
          onClose={() => setCreating(false)}
        />
      )}

      {rolesFor && activeGuild && (
        <MemberRoles
          guild={activeGuild}
          userId={rolesFor.userId}
          name={rolesFor.name}
          onClose={() => setRolesFor(null)}
        />
      )}

      {showMembers && <MemberList onUserMenu={userMenu} />}
    </div>
  );
}
